import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import CheckinCard from '@/components/feature/CheckinCard';
import PollWidget from '@/components/feature/PollWidget';
import LotteryCard from '@/components/feature/LotteryCard';
import { useSeo } from '@/hooks/useSeo';
import { checkin, lotteries, poll } from '@/mocks/gossip';

const anchors = [
  { id: 'lottery', label: '转发抽奖', icon: 'ri-gift-2-line' },
  { id: 'checkin', label: '评论区打卡', icon: 'ri-calendar-check-line' },
  { id: 'vote', label: '粉丝投票', icon: 'ri-bar-chart-2-line' },
];

const rules = [
  {
    id: 'rule-1',
    step: '01',
    title: '选一个玩法',
    desc: '转发抽奖、评论区打卡、粉丝投票可以单独参加，也可以全部参与，互不冲突。',
  },
  {
    id: 'rule-2',
    step: '02',
    title: '按提示完成动作',
    desc: '每个活动都会列出需要完成的步骤，跟着做就行，不需要重复操作来堆数据。',
  },
  {
    id: 'rule-3',
    step: '03',
    title: '等待开奖或即时查看',
    desc: '投票结果即时更新，打卡进度实时累计，转发抽奖在每晚 20:00 集中开奖。',
  },
];

const activityStats = [
  { id: 'as1', label: '进行中活动', value: `${lotteries.length}`, unit: '个' },
  { id: 'as2', label: '今日打卡人数', value: checkin.todayUsers.toLocaleString(), unit: '人' },
  { id: 'as3', label: '累计参与打卡', value: checkin.totalUsers.toLocaleString(), unit: '次' },
  { id: 'as4', label: '本轮投票人数', value: poll.options.reduce((s, o) => s + o.votes, 0).toLocaleString(), unit: '票' },
];

export default function Activities() {
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '互动玩法 - 瓜田福利社',
        description: '福利吃瓜转发抽奖、评论区打卡、粉丝投票三种互动玩法的完整规则与参与入口。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '互动玩法', item: '/activities' },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: '互动玩法 - 福利吃瓜转发抽奖 评论区打卡 粉丝投票 - 瓜田福利社',
    description:
      '解锁福利吃瓜的全部互动玩法：转发抽奖每晚开奖、评论区打卡累计连签、粉丝投票一人一票即时出结果。规则透明，随时参与。',
    keywords: '福利吃瓜转发,福利吃瓜抽奖,福利吃瓜打卡,福利吃瓜粉丝投票,吃瓜互动玩法',
    path: '/activities',
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-[68px]">
        <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-10 bg-background-100 border-b border-background-200">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '互动玩法' }]} />
          <div className="mt-5 max-w-3xl">
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
              互动玩法
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              福利吃瓜转发、抽奖、评论区互动、打卡、粉丝投票，多种玩法等你参与。所有规则都写清楚，参与一次就够，不用反复操作。
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {activityStats.map((item) => (
              <div key={item.id} className="px-4 py-4 rounded-lg bg-background-50 border border-background-200">
                <p className="font-heading text-xl md:text-2xl text-foreground-950">
                  {item.value}
                  <span className="text-xs md:text-sm ml-1 text-foreground-500">{item.unit}</span>
                </p>
                <p className="mt-1 text-xs text-foreground-600">{item.label}</p>
              </div>
            ))}
          </div>

          <nav className="mt-6 flex flex-wrap gap-2" aria-label="玩法快捷导航">
            {anchors.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
              >
                <i className={item.icon}></i>
                {item.label}
              </a>
            ))}
          </nav>
        </section>

        <section id="lottery" className="w-full px-4 md:px-8 py-12 md:py-16 scroll-mt-24">
          <SectionHeading
            eyebrow="玩法一"
            title="福利吃瓜转发抽奖"
            desc="选择你想参加的活动，完成指定动作即可获得抽奖资格。每个账号在单个活动内只计一次参与。"
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {lotteries.map((lottery) => (
              <LotteryCard key={lottery.id} lottery={lottery} />
            ))}
          </div>
        </section>

        <section id="checkin" className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100 scroll-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <SectionHeading
                eyebrow="玩法二"
                title="评论区打卡"
                desc="打卡是最简单也最容易坚持的玩法。每天在任意话题下留一句话，连续天数会自动累计，达到节点解锁对应福利。"
              />
              <ul className="mt-6 space-y-4">
                {[
                  { icon: 'ri-time-line', title: '每天 00:00 刷新', desc: '以自然日为周期，当天任意时间完成评论即算打卡成功。' },
                  { icon: 'ri-fire-line', title: '连续天数实时显示', desc: '昵称旁的打卡标识会同步更新，一目了然。' },
                  { icon: 'ri-gift-line', title: '节点奖励自动解锁', desc: '3 天、7 天、21 天分别对应不同福利，无需手动领取。' },
                ].map((row) => (
                  <li key={row.title} className="flex gap-4">
                    <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-background-50 text-secondary-700 text-lg">
                      <i className={row.icon}></i>
                    </span>
                    <span>
                      <span className="block text-sm md:text-base text-foreground-900">{row.title}</span>
                      <span className="block mt-1 text-sm text-foreground-600 leading-relaxed">{row.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <CheckinCard />
          </div>
        </section>

        <section id="vote" className="w-full px-4 md:px-8 py-12 md:py-16 scroll-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <PollWidget question={poll.question} options={poll.options} />
            <div>
              <SectionHeading
                eyebrow="玩法三"
                title="粉丝投票"
                desc="一人一票，选出本周最想二刷的名场面。投票提交后不可修改，结果即时更新，让排名更接近真实的人数分布。"
              />
              <div className="mt-6 p-5 rounded-lg bg-accent-100 border border-accent-300">
                <h3 className="font-heading text-lg text-accent-900 flex items-center gap-2">
                  <i className="ri-lightbulb-line"></i>
                  投票小提示
                </h3>
                <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                  投票选项会随每周话题更新。如果你希望某个名场面进入候选，可以在评论区打卡时一并提名，我们会在下一期整理高赞提名。
                </p>
              </div>
              <Link
                to="/topics"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md border border-foreground-900 text-foreground-900 text-sm whitespace-nowrap hover:bg-foreground-900 hover:text-background-50 transition-colors"
              >
                先去逛逛话题
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
          <SectionHeading eyebrow="参与流程" title="三步就能参与" align="center" />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {rules.map((rule) => (
              <div key={rule.id} className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
                <span className="font-heading text-3xl text-primary-300">{rule.step}</span>
                <h3 className="mt-3 font-heading text-xl text-foreground-950">{rule.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{rule.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}